import CustomModal from "components/common/modal";
import TableWrapper from "components/common/table-wrapper";
import Tooltip from "components/common/tooltip";
import LcmHealthPlanForm from "components/lcm-healthplans/lcm-healthplan-form/lcmHealthplanForm";
import {
  DEBOUNCE_DELAY,
  defaultPaginationObject,
  orderStratergy,
} from "constants/common";
import debounce from "lodash/debounce";
import { Pencil, Plus, Trash, TrashSimple } from "phosphor-react";
import { useCallback, useEffect, useState } from "react";
import {
  capitalizeName,
  highlightFirstMatch,
  getUserRole,
  canViewOnlyHealthPlans,
} from "utils/common";
import {
  deleteLcmHealthPlan,
  getLcmHealthPlans,
  deleteBulkHealthPlans,
} from "./api";

const LCMHealthPlans = () => {
  const [lcmHealthPlans, setLcmHealthPlans] = useState([]);
  const [pagination, setPagination] = useState(defaultPaginationObject);
  const [filters, setFilters] = useState({
    search: "",
    status: null,
  });
  const [meta, setMeta] = useState({ sort: "", order: "asc" });
  const [isSorted, setSorted] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [search, setSearch] = useState("");
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isAddEditModalVisible, setAddEditModalVisible] = useState(false);
  const [selectedHealthPlan, setSelectedHealthPlan] = useState(null);
  const [refreshPage, setRefreshPage] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Get user role and permissions
  const userRole = getUserRole();
  const isViewOnly = canViewOnlyHealthPlans(userRole);
  // Note: Admin users (with admin flag) automatically get full access via canViewOnlyHealthPlans returning false

  const defaultPayload = {
    ...pagination,
    "filter[status]": filters.status,
    query: filters.search,
  };

  const handleRefreshPage = () => {
    setRefreshPage((pre) => !pre);
    setSelectedItems([]); // reset selection
    setSelectAll(false);
  };

  const handleSetSearchQuery = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
  };

  const debouncedSearch = useCallback(
    debounce(handleSetSearchQuery, DEBOUNCE_DELAY),
    [],
  );

  const handlePageChange = (value) => {
    setPagination({ ...pagination, page: value });
  };

  const fetchlcmHealthPlansList = async (payload) => {
    setLoading(true);
    try {
      const result = await getLcmHealthPlans(payload);
      if (result?.health_plans) {
        setLcmHealthPlans(
          result?.health_plans.map((plan) => ({
            ...plan,
            checked: false,
          })),
        );
        setCount(result?.meta?.total_count);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const updated = lcmHealthPlans.map((item) => ({
      ...item,
      checked: newSelectAll,
    }));
    setLcmHealthPlans(updated);
    const selected = newSelectAll ? updated.map((item) => item.id) : [];
    setSelectedItems(selected);
  };

  const handleCheckboxChange = (index) => {
    const updatedList = [...lcmHealthPlans];
    updatedList[index].checked = !updatedList[index].checked;
    setLcmHealthPlans(updatedList);

    const selected = updatedList
      .filter((item) => item.checked)
      .map((item) => item.id);

    setSelectedItems(selected);
    setSelectAll(selected.length === updatedList.length);
  };

  const toggleAddEditModal = () => {
    setAddEditModalVisible((prev) => !prev);
  };

  const sortedDataByColumn = (columnName, isAlreadySorted) => {
    if (lcmHealthPlans.length > 0) {
      setMeta((pre) => ({
        ...pre,
        sort: columnName,
        order: orderStratergy[pre.order],
      }));
    }
  };

  const handleDeleteHealthPlan = async (id) => {
    const result = await deleteLcmHealthPlan(id);
    if (result) handleRefreshPage();
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    const result = await deleteBulkHealthPlans(selectedItems);
    if (result) handleRefreshPage();
  };

  useEffect(() => {
    const payload = {
      ...defaultPayload,
      order: meta.order,
      sort: meta.sort,
    };

    fetchlcmHealthPlansList(payload);
  }, [pagination, filters, meta, refreshPage]);

  return (
    <div className="main-contents">
      <h1>Health Plans</h1>
      <TableWrapper
        searchPlaceholder="Search Health Plan"
        setSearhQuery={(query) => {
          setSearch(query);
          debouncedSearch(query);
        }}
        searchValue={search}
        totalListCount={count}
        pageSize={pagination.per}
        currentPage={pagination.page}
        isLoading={loading}
        onPageChange={handlePageChange}
        actionButtons={
          isViewOnly
            ? [] // No action buttons for view-only users
            : selectedItems.length > 0
            ? [
                {
                  label: `Delete ${selectedItems.length} Items`,
                  classes: "danger-btn m-0-imp",
                  icon: <TrashSimple size={24} className="me-3" />,
                  handleClick: handleBulkDelete,
                },
              ]
            : [
                {
                  label: "Add Health Plan",
                  classes: "primary-btn",
                  icon: <Plus size={24} className="me-3" />,
                  handleClick: toggleAddEditModal,
                },
              ]
        }
      >
        <table className="table table-rows">
          <thead className="table-header">
            <tr>
              {!isViewOnly && (
                <th>
                  <div className="form-check ps-3 mb-0">
                    <input
                      className="form-check-input light-grey-background"
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </div>
                </th>
              )}
              <th scope="col">
                <div className="header-text-otr">
                  <div
                    className="table-name heading-xsb cursor-pointer c-hand"
                    onClick={() => sortedDataByColumn("name", isSorted)}
                  >
                    Name
                  </div>
                </div>
              </th>
              <th scope="col">
                <div className="header-text-otr">
                  <div
                    className="table-name heading-xsb cursor-pointer c-hand"
                    onClick={() =>
                      sortedDataByColumn("auto_assignment", isSorted)
                    }
                  >
                    Status
                  </div>
                </div>
              </th>
              {!isViewOnly && <th scope="col"></th>}
            </tr>
          </thead>
          <tbody>
            {lcmHealthPlans.map((healthPlan, index) => (
              <tr key={index}>
                {!isViewOnly && (
                  <td>
                    <div className="form-check ps-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={healthPlan.checked || false}
                        onChange={() => handleCheckboxChange(index)}
                      />
                    </div>
                  </td>
                )}
                <td className="main-heading">
                  {highlightFirstMatch(
                    `${capitalizeName(healthPlan.name)} `,
                    filters.search,
                  )}
                </td>
                <td>
                  <div
                    className={`status ${
                      healthPlan.status === "active" ? "active" : "inactive"
                    }`}
                  >
                    {healthPlan?.status === "active"
                      ? "Active"
                      : "Inactive" || ""}
                  </div>
                </td>
                {!isViewOnly && (
                  <td>
                    <Tooltip title="Edit">
                      <Pencil
                        size={24}
                        className="opacity-50 me-4"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedHealthPlan(healthPlan);
                          toggleAddEditModal();
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Delete">
                      <Trash
                        size={24}
                        className="opacity-50 me-4"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          healthPlan?.id &&
                            handleDeleteHealthPlan(healthPlan.id);
                        }}
                      />
                    </Tooltip>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrapper>

      <CustomModal
        size="sm"
        show={isAddEditModalVisible}
        onHide={() => {
          toggleAddEditModal();
          setSelectedHealthPlan(null);
        }}
        heading={`${selectedHealthPlan ? "Edit" : "Add"} Heath Plans`}
      >
        <LcmHealthPlanForm
          healthPlan={selectedHealthPlan}
          handleClose={() => {
            toggleAddEditModal();
            setSelectedHealthPlan(null);
          }}
          onSuccess={() => {
            toggleAddEditModal();
            handleRefreshPage();
            setSelectedHealthPlan(null);
          }}
        />
      </CustomModal>
    </div>
  );
};

export default LCMHealthPlans;
